import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { HeaderuserMenuComponent } from '@user/headeruser-menu/headeruser-menu.component';
import { filter } from 'rxjs';
import { WcgChipModule, WcgChipType } from '@wcg/frontend-framework';
import {
  RolePermission,
  PermissionNames,
} from '@shared/interfaces/permissions.interface';
import environment from '@environments/environment';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { AuthService } from '@auth/auth.service';
import { IUser } from '@shared/user.interface';
import { AppMenuNavOption } from '@data-access/shared';

@Component({
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTabsModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    MatDividerModule,
    MatGridListModule,
    WcgChipModule,
    HeaderuserMenuComponent,
    RouterModule,
    MatTooltipModule,
    NgClass,
  ],
  selector: 'wcg-main-header',
  styleUrls: ['./main-header.component.scss'],
  templateUrl: './main-header.component.html',
})
export class MainHeaderComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _authService = inject(AuthService);

  readonly $isAuthenticated = signal<boolean>(false);
  readonly $isInternalUser = signal<boolean>(false);
  readonly $isImpersonating = signal<boolean>(false);
  readonly $impersonationText = signal<string>('');
  readonly $activeLink = signal<string>('');
  readonly adminMenuOpen = signal<boolean>(false);
  readonly profileMenuOpen = signal<boolean>(false);
  chipType: WcgChipType = WcgChipType.INFO;

  readonly $menuLinks = signal<AppMenuNavOption[]>([]);
  readonly $adminLinks = signal<AppMenuNavOption[]>([]);
  readonly $profileLinks = signal<AppMenuNavOption[]>([]);
  private readonly _$currentUser = toSignal(
    this._authService.user$.pipe(filter(Boolean)),
  );

  ctrlPressed = false;

  readonly impersonateBanner = input<boolean>(false);

  constructor() {
    this._customListener();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    this.ctrlPressed = event.ctrlKey || event.key?.startsWith('F');
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.ctrlPressed = false;
    }
  }

  ngOnInit() {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event: NavigationEnd) => {
        this.$activeLink.set(event.urlAfterRedirects);
      });
  }

  onTabChange(routeLink: string, event: Event) {
    event.preventDefault();
    if (this.ctrlPressed) {
      window.open(routeLink, '_blank');
    }
    if (!this.ctrlPressed) {
      this._router.navigate([routeLink]);
    }
  }

  getActiveRouteDomain(route: string) {
    const domain = route.split('/');
    return this.$activeLink().includes(domain[1]);
  }

  private _buildLinks(user?: IUser) {
    const menuLinks = [
      { label: 'Submissions', route: '/welcome' },
      { label: 'Studies', route: '/studies/home' },
      { label: 'Sites', route: '/sites/home' },
    ];

    this.$menuLinks.set(menuLinks);

    if (!user) return;

    // Handle People link
    const hasPeopleAdmin = user.role.permissions.some(
      (permission: RolePermission) =>
        permission.name === PermissionNames.PeopleAdmin &&
        permission.can_view == true,
    );

    const canViewOrganizations = user.role.permissions.some(
      (permission: RolePermission) =>
        permission.name === PermissionNames.ViewOrganizationEnum &&
        permission.can_view === true,
    );

    /**
     * @HANDLE PROFILE LINKS
     * */

    this.$profileLinks.set([]);
    if (canViewOrganizations) {
      this.$profileLinks.update((links) => {
        links.push({
          label: 'Organizations',
          route: '/organizations',
        });
        return links;
      });
    }
    if (hasPeopleAdmin) {
      this.$profileLinks.update((links) => {
        links.push({ label: 'People', route: '/profiles/people/home' });
        return links;
      });
    }

    // Handle admin links
    this.$adminLinks.set([]);
    if (user.is_internal) {
      const permissionToRouteMap = {
        [PermissionNames.RoleAdmin]: { label: 'Roles', route: '/admin/roles' },
        [PermissionNames.QuestionnaireAdmin]: {
          label: 'Questionnaire',
          route: '/admin/questionnaire',
        },
        [PermissionNames.NotificationAdmin]: {
          label: 'Notifications',
          route: '/admin/notifications',
        },
        [PermissionNames.ConfigurationAdmin]: {
          label: 'Configuration',
          route: '/admin/configurations',
        },
      };

      // Add admin links based on permissions
      Object.entries(permissionToRouteMap).forEach(([permission, linkData]) => {
        if (
          user.role.permissions.some(
            (role: RolePermission) => role.name === permission,
          )
        ) {
          this.$adminLinks.update((links) => {
            links.push(linkData);
            return links;
          });
        }
      });

      // Handle IT Admin specific links
      if (
        user.role.permissions.some(
          (permission: RolePermission) =>
            permission.name === PermissionNames.AuditAdmin &&
            permission.can_edit === true,
        )
      ) {
        this.$adminLinks.update((links) => {
          links.push({ label: 'Audit', route: '/audit' });
          return links;
        });
        if ('enableMockIris' in environment && environment.enableMockIris) {
          this.$adminLinks.update((links) => {
            links.push({ label: 'Mock Iris', route: '/mock-iris' });
            return links;
          });
        }
      }
    }

    // Update user state
    this.$isAuthenticated.set(true);
    this.$isInternalUser.set(!!user.is_internal);
  }

  private _customListener() {
    toObservable(this._$currentUser)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((user) => {
        this._buildLinks(user as IUser);
      });
  }
}
